// Sentry implementation of the WebView `CrashReporter`.
//
// The ~4 MB `@sentry/react` SDK is dynamically imported on first init, so
// DSN-less / no-consent / dev builds never pull it into the cold-boot
// module graph. Consent revocation drops events via `beforeSend` (an
// in-memory flag) rather than tearing the client down — no re-init churn,
// events resume the instant consent flips back.

import type { CrashConfig, CrashReporter } from './reporter';
import { scrubUrl } from './reporter';

export class SentryReporter implements CrashReporter {
  // In-memory mirror of the consent flag, read synchronously by beforeSend.
  private consent = false;
  // Guard so init() runs the SDK setup at most once.
  private initStarted = false;

  setConsent(granted: boolean): void {
    this.consent = granted;
  }

  init(cfg: CrashConfig): void {
    if (this.initStarted || !cfg.dsn) return;
    this.initStarted = true;
    void (async () => {
      const Sentry = await import('@sentry/react');
      Sentry.init({
        dsn: cfg.dsn,
        release: cfg.release,
        environment: cfg.environment,
        integrations: [Sentry.browserTracingIntegration()],
        tracesSampleRate: cfg.tracesSampleRate,
        // Drop every event while consent is revoked, without tearing down
        // the client. Events resume the instant consent flips back true.
        beforeSend: (event) => (this.consent ? event : null),
        // `beforeSend` only sees error events; performance transactions
        // (emitted via `tracesSampleRate` + browserTracing) have their own
        // hook. Gate them on consent too, or revoked users still ship
        // transactions.
        beforeSendTransaction: (event) => (this.consent ? event : null),
        // Scrub URLs in breadcrumbs — Songbird project paths and asset
        // URLs can embed user filesystem paths.
        beforeBreadcrumb: (crumb) => {
          if (crumb.data && typeof crumb.data === 'object' && 'url' in crumb.data) {
            crumb.data.url = scrubUrl(String(crumb.data.url));
          }
          return crumb;
        },
      });
      Sentry.setTag('layer', 'webview');
    })();
  }
}
